import { collectionsConfig, config } from 'lib/config'
import { map, mergeMap, Observable, timer } from 'rxjs'
import { ajax } from 'rxjs/ajax'

export interface Response {
  count: number
  total: number
  pageToken?: string
  startIndex?: number
  data: Data
}

export interface Location {
  address?: Address
  position: Position
}

export interface Address {
  text: string
}

export interface Position {
  latitude: number
  longitude: number
}

export type Data = DataItem[]

export interface DataItem {
  application: string
  collectionId: string[]
  createdTime: number
  creatorId: string
  customName: string
  deleteAccess: string
  deleted: boolean
  description?: string
  id: string
  placesForeignId?: string
  placesId?: string
  instanceVersion: string
  location: Location
  name: string
  readAccess: string
  sor: string
  type: string
  updatedTime: number
  writeAccess: string
}

interface CreateFavoritePlaceResponse {
  id: string
}

export interface FavoritePlaceBody {
  customName: string
  location: {
    address: {
      text: string
    }
    position: {
      latitude: number
      longitude: number
    }
  }
  name: string
  placesForeignId: string
}

const placeType = 'favoritePlace'

export const httpGetCollectionPlaces = (
  accessToken: string,
  collectionId: string,
  pageToken?: string,
  delay?: number,
  deleted = false,
  count = collectionsConfig.placePageSize
): Observable<Response> => {
  const url = new URL(`${config.SCBE_URL}/scbe/v20/${placeType}`)
  url.searchParams.set('q', `collectionId=${collectionId}`)
  url.searchParams.set('count', `${count}`)

  if (pageToken !== undefined) {
    url.searchParams.set('pageToken', `${pageToken}`)
  }

  if (deleted !== undefined) {
    url.searchParams.set('deleted', Boolean(deleted).toString())
  }

  const delayNumber = delay !== undefined ? delay : 0

  return timer(delayNumber).pipe(
    mergeMap(() =>
      ajax({
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        method: 'GET',
        url: url.pathname + url.search,
      }).pipe(map((ajax) => ajax.response as Response))
    )
  )
}

export const httpCreateFavoritePlace = (
  accessToken: string,
  favoritePlaceBody: FavoritePlaceBody
): Observable<CreateFavoritePlaceResponse> => {
  const url = new URL(`${config.SCBE_URL}/scbe/v20/${placeType}`)

  return ajax({
    body: {
      ...favoritePlaceBody,
      type: placeType,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    method: 'POST',
    url: url.pathname,
  }).pipe(map((ajax) => ajax.response as CreateFavoritePlaceResponse))
}

export const httpAssignPlaceToCollection = (
  accessToken: string,
  collectionId: string,
  placeId: string
): Observable<DataItem> => {
  const url = new URL(
    `${config.SCBE_URL}/scbe/v20/collection/${collectionId}/${placeType}/${placeId}`
  )

  return ajax({
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    method: 'PUT',
    url: url.pathname,
  }).pipe(map((ajax) => ajax.response as DataItem))
}

export const httpDeletePlaceFromCollection = (
  accessToken: string,
  scbePlaceId: string,
  collectionId: string
): Observable<void> => {
  const url = new URL(
    `${config.SCBE_URL}/scbe/v20/collection/${collectionId}/${placeType}/${scbePlaceId}`
  )

  return ajax({
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    method: 'DELETE',
    url: url.pathname,
  }).pipe(
    map((_) => {
      return
    })
  )
}
